import React, { useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../api';
import { useToast } from '../components/Toast';

const RegisterPage = ({ navigate }) => {
  const toast = useToast();
  const errorRef = useRef(null);
  const [step, setStep] = useState(1); // 1: Register Form, 2: OTP Verification
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    postal_code: ''
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isEmailExists, setIsEmailExists] = useState(false);

  // Parse backend errors — handles string, {error}, {detail}, or field-level objects
  const parseBackendError = (data) => {
    if (!data) return { general: 'An unknown error occurred.' };
    if (typeof data === 'string') return { general: data };
    if (data.error) return { general: data.error };
    if (data.detail) return { general: data.detail };
    if (data.non_field_errors) return { general: data.non_field_errors.join(', ') };

    // Field-level errors e.g. { email: ['already exists'], password: ['too short'] }
    const fieldMap = {};
    let hasFields = false;
    Object.entries(data).forEach(([field, msgs]) => {
      fieldMap[field] = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
      hasFields = true;
    });
    if (hasFields) return { fields: fieldMap };
    return { general: 'Registration failed. Please check your details.' };
  };

  const showError = (msg) => {
    setError(msg);
    // Scroll error into view after render
    setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setIsEmailExists(false);

    if (formData.password !== formData.confirmPassword) {
      showError('Passwords do not match. Please re-enter.');
      return;
    }
    if (formData.password.length < 6) {
      showError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let data = null;
      try { data = await response.json(); } catch { data = null; }

      if (response.ok) {
        setStep(2);
        toast.success('OTP sent to your email. Please check your inbox.');
      } else {
        const parsed = parseBackendError(data);
        if (parsed.fields) {
          setFieldErrors(parsed.fields);
          // Build a readable summary
          const lines = Object.entries(parsed.fields).map(
            ([f, msg]) => `${f.charAt(0).toUpperCase() + f.slice(1)}: ${msg}`
          );
          showError(lines.join('\n'));
          // Highlight email-exists case
          if (parsed.fields.email?.toLowerCase().includes('already')) {
            setIsEmailExists(true);
          }
        } else {
          const msg = parsed.general || 'Registration failed. Please try again.';
          if (msg.toLowerCase().includes('already')) setIsEmailExists(true);
          showError(msg);
        }
      }
    } catch (err) {
      if (err.name === 'TypeError' || err.message?.includes('fetch')) {
        showError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        showError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp })
      });

      let data = null;
      try { data = await response.json(); } catch { data = null; }

      if (response.ok) {
        toast.success('Registration successful! You can now login.');
        navigate('login');
      } else {
        const parsed = parseBackendError(data);
        const msg = parsed.general || 'OTP verification failed. Please check the code and try again.';
        showError(msg);
      }
    } catch {
      showError('Unable to connect to the server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar navigate={navigate} />
      
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cream via-white to-sand py-12 px-4 pt-28">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 animate-scale-in">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🕌</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-2">
                {step === 1 ? 'Create Account' : 'Verify OTP'}
              </h2>
              <p className="text-ink/65">
                {step === 1 ? 'Join us for your spiritual journey' : 'Enter the OTP sent to your email'}
              </p>
            </div>

            {error && (
              <div ref={errorRef} className="bg-red-50 border border-red-300 rounded-xl px-4 py-4 mb-6 animate-fade-in">
                <div className="flex items-start gap-3">
                  <span className="text-red-500 text-xl shrink-0 mt-0.5">⚠️</span>
                  <div className="flex-1">
                    <p className="font-semibold text-red-700 text-sm mb-1">
                      {step === 1 ? 'Registration Error' : 'Verification Error'}
                    </p>
                    {error.includes('\n') ? (
                      <ul className="text-red-600 text-sm space-y-1 list-disc list-inside">
                        {error.split('\n').map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-red-600 text-sm">{error}</p>
                    )}
                    {isEmailExists && (
                      <button
                        onClick={() => navigate('login')}
                        className="mt-2 text-sm font-bold text-red-800 underline hover:text-red-900 flex items-center gap-1">
                        → Click here to Login instead
                      </button>
                    )}
                  </div>
                  <button onClick={() => { setError(''); setFieldErrors({}); }} className="text-red-400 hover:text-red-600 shrink-0 text-lg leading-none">✕</button>
                </div>
              </div>
            )}

            {step === 1 ? (
              <form onSubmit={handleRegister} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.name ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      placeholder="John Doe"
                    />
                    {fieldErrors.name && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      placeholder="your@email.com"
                    />
                    {fieldErrors.email && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.phone ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      placeholder="+1 234 567 8900"
                    />
                    {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Postal Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.postal_code ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      placeholder="12345"
                    />
                    {fieldErrors.postal_code && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.postal_code}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Address *
                    </label>
                    <textarea
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.address ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      rows="2"
                      placeholder="123 Main Street, City"
                    />
                    {fieldErrors.address && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.address}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${fieldErrors.password ? 'border-red-400 bg-red-50' : 'border-sand'}`}
                      placeholder="••••••••"
                    />
                    {fieldErrors.password && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink/80 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                        error.includes('Passwords do not match') ? 'border-red-400 bg-red-50' : 'border-sand'
                      }`}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3 rounded-lg font-bold hover:from-secondary hover:to-ink transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Sending OTP...' : 'Register & Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div className="bg-cream border border-sand rounded-lg p-4 mb-6">
                  <p className="text-primary text-sm">
                    📧 OTP has been sent to <strong>{formData.email}</strong>
                  </p>
                  <p className="text-primary text-xs mt-2">
                    Please check your email inbox and spam folder
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink/80 mb-2">
                    Enter 6-Digit OTP
                  </label>
                  <input
                    type="text"
                    required
                    maxLength="6"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-center text-2xl tracking-widest"
                    placeholder="000000"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3 rounded-lg font-bold hover:from-secondary hover:to-ink transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full border-2 border-sand text-ink/80 py-3 rounded-lg font-semibold hover:bg-cream transition-all">
                  Back to Registration
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-ink/65">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('login')}
                  className="text-primary font-semibold hover:text-secondary">
                  Login Now
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default RegisterPage;
